import { c as _c } from "react/compiler-runtime";
import type { SourceCitationEvidence } from '@onefootprint/request-types/dashboard';
import type { ReactNode } from 'react';
import HighlightedJson from '../highlighted-json';
type HighlightProps = {
  evidence: SourceCitationEvidence;
  // The source's decrypted content, for highlighters that render the cited node in context.
  output: unknown;
};
type Highlighter = (props: HighlightProps) => ReactNode;

// The cited node within a source's JSON output, by RFC-6901 pointer. The backend has already
// resolved + validated the pointer, so we render the output with that node highlighted in context
// (rather than as a detached snippet).
const JsonHighlight: Highlighter = t0 => {
  const $ = _c(9);
  const {
    evidence,
    output
  } = t0;
  if (evidence.kind !== "json") {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-label-2 text-tertiary">Cited evidence</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== evidence.data.pointer) {
    t2 = <div className="flex items-center justify-between gap-2">{t1}<code className="truncate font-mono text-snippet-3 text-tertiary">{evidence.data.pointer}</code></div>;
    $[1] = evidence.data.pointer;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== evidence.data.pointer || $[4] !== output) {
    t3 = <HighlightedJson value={output} pointer={evidence.data.pointer} />;
    $[3] = evidence.data.pointer;
    $[4] = output;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex min-w-0 flex-col gap-1">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};

// Adding an evidence kind (document text range, image region, …) is a new entry here plus its
// backend resolver — the popover and the rest of the citation pipeline are untouched.
const CITATION_HIGHLIGHTERS: Record<SourceCitationEvidence['kind'], Highlighter> = {
  json: JsonHighlight
};
const CitationHighlight = t0 => {
  const $ = _c(4);
  const {
    evidence,
    output
  } = t0;
  const Highlighter = CITATION_HIGHLIGHTERS[evidence.kind];
  let t1;
  if ($[0] !== Highlighter || $[1] !== evidence || $[2] !== output) {
    t1 = Highlighter ? <Highlighter evidence={evidence} output={output} /> : null;
    $[0] = Highlighter;
    $[1] = evidence;
    $[2] = output;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export default CitationHighlight;